import React from 'react';
import { 
  FaChartLine, 
  FaChartBar, 
  FaChartPie, 
  FaGlobe, 
  FaPercentage, 
  FaCalendarAlt, 
  FaCheck,
  FaLanguage,
  FaBook,
  FaCrown,
  FaPlus,
  FaEdit,
  FaTrash,
  FaDatabase,
  FaFileExcel
} from 'react-icons/fa';
import useProverbs from '../hooks/useProverbs';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import useNotification from '../hooks/useNotification';

const AnalyticsPage = () => {
  const { user } = useAuth();
  const { proverbs, stats } = useProverbs();
  const { showNotification } = useNotification();

  const isSuperAdmin = () => user?.role_id === 1;

  const recentActivities = [];

  const calculateAdvancedStats = () => {
    const languageCompleteness = proverbs.map(proverb => {
      const completeness = {
        id: proverb.id,
        chinese: proverb.chinese_proverb ? 1 : 0,
        pinyin: proverb.pinyin ? 1 : 0,
        english: proverb.english_translation ? 1 : 0,
        english_explanation: proverb.english_explanation ? 1 : 0,
        english_equivalent: proverb.english_equivalent ? 1 : 0,
        french: proverb.french_translation ? 1 : 0,
        french_explanation: proverb.french_explanation ? 1 : 0,
        french_equivalent: proverb.french_equivalent ? 1 : 0,
        arabic: proverb.arabic_translation ? 1 : 0,
        arabic_explanation: proverb.arabic_Explanation ? 1 : 0,
      };
      
      const totalFields = 10;
      const completedFields = Object.values(completeness).reduce((sum, val) => sum + val, 0) - 1;
      completeness.completionPercentage = Math.round((completedFields / (totalFields - 1)) * 100);
      
      return completeness;
    });

    const qualityMetrics = {
      fullyComplete: languageCompleteness.filter(p => p.completionPercentage === 100).length,
      mostlyComplete: languageCompleteness.filter(p => p.completionPercentage >= 75 && p.completionPercentage < 100).length,
      partiallyComplete: languageCompleteness.filter(p => p.completionPercentage >= 50 && p.completionPercentage < 75).length,
      incomplete: languageCompleteness.filter(p => p.completionPercentage < 50).length,
      averageCompletion: Math.round(languageCompleteness.reduce((sum, p) => sum + p.completionPercentage, 0) / languageCompleteness.length),
      
      chineseCompletion: Math.round((stats.chinese / stats.total) * 100),
      englishCompletion: Math.round((stats.english / stats.total) * 100),
      frenchCompletion: Math.round((stats.french / stats.total) * 100),
      arabicCompletion: Math.round((stats.arabic / stats.total) * 100),
      
      withExplanations: proverbs.filter(p => p.english_explanation || p.french_explanation || p.arabic_Explanation).length,
      withEquivalents: proverbs.filter(p => p.english_equivalent || p.french_equivalent).length,
      
      averageChineseLength: Math.round(proverbs.filter(p => p.chinese_proverb).reduce((sum, p) => sum + p.chinese_proverb.length, 0) / stats.chinese),
      averageEnglishLength: Math.round(proverbs.filter(p => p.english_translation).reduce((sum, p) => sum + p.english_translation.length, 0) / stats.english),
      averageFrenchLength: Math.round(proverbs.filter(p => p.french_translation).reduce((sum, p) => sum + p.french_translation.length, 0) / stats.french),
      averageArabicLength: Math.round(proverbs.filter(p => p.arabic_translation).reduce((sum, p) => sum + p.arabic_translation.length, 0) / stats.arabic),
    };

    return { languageCompleteness, qualityMetrics };
  };

  const exportAnalyticsToExcel = () => {
    const { languageCompleteness, qualityMetrics } = calculateAdvancedStats();
    
    const workbook = XLSX.utils.book_new();
    
    const overviewData = [
      ['Chinese Proverbs Dashboard - Analytics Report'],
      ['Generated on:', new Date().toLocaleString()],
      ['Total Proverbs:', stats.total],
      [''],
      ['Language Distribution:'],
      ['Chinese:', stats.chinese, `${qualityMetrics.chineseCompletion}%`],
      ['English:', stats.english, `${qualityMetrics.englishCompletion}%`],
      ['French:', stats.french, `${qualityMetrics.frenchCompletion}%`],
      ['Arabic:', stats.arabic, `${qualityMetrics.arabicCompletion}%`],
      [''],
      ['Quality Metrics:'],
      ['Fully Complete (100%):', qualityMetrics.fullyComplete],
      ['Mostly Complete (75-99%):', qualityMetrics.mostlyComplete],
      ['Partially Complete (50-74%):', qualityMetrics.partiallyComplete],
      ['Incomplete (<50%):', qualityMetrics.incomplete],
      ['Average Completion:', `${qualityMetrics.averageCompletion}%`],
    ];
    
    const overviewSheet = XLSX.utils.aoa_to_sheet(overviewData);
    XLSX.utils.book_append_sheet(workbook, overviewSheet, 'Overview');
    
    const fileName = `proverbs-analytics-${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    
    showNotification(`Analytics report exported successfully: ${fileName}`, 'success');
  };

  return (
    <div className="analytics-section">
      <div className="analytics-header">
        <h2><FaChartLine /> Analytics & Insights</h2>
        {isSuperAdmin() && (
          <button onClick={exportAnalyticsToExcel} className="export-analytics-btn">
            <FaFileExcel /> Export Analytics Report
          </button>
        )}
      </div>

      <div className="analytics-grid">
        {/* Overview Stats */}
        <div className="analytics-card overview-card">
          <h3><FaChartBar /> Collection Overview</h3>
          <div className="overview-stats">
            <div className="stat-item">
              <FaBook className="stat-icon" />
              <div>
                <div className="stat-number">{stats.total}</div>
                <div className="stat-label">Total Proverbs</div>
              </div>
            </div>
            <div className="stat-item">
              <FaGlobe className="stat-icon" />
              <div>
                <div className="stat-number">{Object.values(stats).filter((val, i) => i > 0 && val > 0).length}</div>
                <div className="stat-label">Languages</div>
              </div>
            </div>
            <div className="stat-item">
              <FaPercentage className="stat-icon" />
              <div>
                <div className="stat-number">{stats.total ? Math.round((stats.english / stats.total) * 100) : 0}%</div>
                <div className="stat-label">Completion Rate</div>
              </div>
            </div>
          </div>
        </div>

        {/* Language Distribution */}
        <div className="analytics-card">
          <h3><FaChartPie /> Language Distribution</h3>
          <div className="language-stats">
            <div className="stat-bar">
              <div className="stat-info">
                <span>🇨🇳 Chinese: {stats.chinese}</span>
                <span className="percentage">{stats.total ? Math.round((stats.chinese / stats.total) * 100) : 0}%</span>
              </div>
              <div className="bar">
                <div 
                  className="bar-fill chinese"
                  style={{width: `${stats.total ? (stats.chinese / stats.total) * 100 : 0}%`}}
                ></div>
              </div>
            </div>
            <div className="stat-bar">
              <div className="stat-info">
                <span>🇺🇸 English: {stats.english}</span>
                <span className="percentage">{stats.total ? Math.round((stats.english / stats.total) * 100) : 0}%</span>
              </div>
              <div className="bar">
                <div 
                  className="bar-fill english"
                  style={{width: `${stats.total ? (stats.english / stats.total) * 100 : 0}%`}}
                ></div>
              </div>
            </div>
            <div className="stat-bar">
              <div className="stat-info">
                <span>🇫🇷 French: {stats.french}</span>
                <span className="percentage">{stats.total ? Math.round((stats.french / stats.total) * 100) : 0}%</span>
              </div>
              <div className="bar">
                <div 
                  className="bar-fill french"
                  style={{width: `${stats.total ? (stats.french / stats.total) * 100 : 0}%`}}
                ></div>
              </div>
            </div>
            <div className="stat-bar">
              <div className="stat-info">
                <span>🇸🇦 Arabic: {stats.arabic}</span>
                <span className="percentage">{stats.total ? Math.round((stats.arabic / stats.total) * 100) : 0}%</span>
              </div>
              <div className="bar">
                <div 
                  className="bar-fill arabic"
                  style={{width: `${stats.total ? (stats.arabic / stats.total) * 100 : 0}%`}}
                ></div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Quality Metrics */}
        <div className="analytics-card">
          <h3><FaCheck /> Quality Metrics</h3>
          <div className="quality-metrics">
            <div className="metric-row">
              <span className="metric-label">Translation Coverage:</span>
              <div className="metric-grid">
                <div className="metric-item">
                  <span>English</span>
                  <span className="metric-value success">{stats.total ? Math.round((stats.english / stats.total) * 100) : 0}%</span>
                </div>
                <div className="metric-item">
                  <span>French</span>
                  <span className="metric-value warning">{stats.total ? Math.round((stats.french / stats.total) * 100) : 0}%</span>
                </div>
                <div className="metric-item">
                  <span>Arabic</span>
                  <span className="metric-value info">{stats.total ? Math.round((stats.arabic / stats.total) * 100) : 0}%</span>
                </div>
              </div>
            </div>
            
            <div className="metric-row">
              <span className="metric-label">Content Richness:</span>
              <div className="metric-grid">
                <div className="metric-item">
                  <span>With Explanations</span>
                  <span className="metric-value">{proverbs.filter(p => p.english_explanation || p.french_explanation || p.arabic_Explanation).length}</span>
                </div>
                <div className="metric-item">
                  <span>With Equivalents</span>
                  <span className="metric-value">{proverbs.filter(p => p.english_equivalent || p.french_equivalent).length}</span>
                </div>
                <div className="metric-item">
                  <span>Complete Entries</span>
                  <span className="metric-value success">{proverbs.filter(p => p.chinese_proverb && p.pinyin && p.english_translation).length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content Analysis */}
        <div className="analytics-card">
          <h3><FaLanguage /> Content Analysis</h3>
          <div className="content-analysis">
            <div className="analysis-item">
              <span className="analysis-label">Multilingual Proverbs:</span>
              <span className="analysis-value">
                {proverbs.filter(p => 
                  [p.english_translation, p.french_translation, p.arabic_translation]
                    .filter(Boolean).length >= 2
                ).length}
              </span>
            </div>
            <div className="analysis-item">
              <span className="analysis-label">Fully Translated:</span>
              <span className="analysis-value">
                {proverbs.filter(p => 
                  p.english_translation && p.french_translation && p.arabic_translation
                ).length}
              </span>
            </div>
            <div className="analysis-item">
              <span className="analysis-label">Average Chinese Length:</span>
              <span className="analysis-value">
                {stats.chinese > 0 ? Math.round(proverbs.filter(p => p.chinese_proverb).reduce((sum, p) => sum + p.chinese_proverb.length, 0) / stats.chinese) : 0} chars
              </span>
            </div>
            <div className="analysis-item">
              <span className="analysis-label">Average English Length:</span>
              <span className="analysis-value">
                {stats.english > 0 ? Math.round(proverbs.filter(p => p.english_translation).reduce((sum, p) => sum + p.english_translation.length, 0) / stats.english) : 0} chars
              </span>
            </div>
          </div>
        </div>

        {/* Recent Activity Summary */}
        <div className="analytics-card">
          <h3><FaCalendarAlt /> Activity Summary</h3>
          <div className="activity-summary">
            <div className="activity-stat">
              <FaPlus className="activity-icon create" />
              <div>
                <div className="activity-count">{recentActivities.filter(a => a.type === 'create').length}</div>
                <div className="activity-label">Created</div>
              </div>
            </div>
            <div className="activity-stat">
              <FaEdit className="activity-icon update" />
              <div>
                <div className="activity-count">{recentActivities.filter(a => a.type === 'update').length}</div>
                <div className="activity-label">Updated</div>
              </div>
            </div>
            <div className="activity-stat">
              <FaTrash className="activity-icon delete" />
              <div>
                <div className="activity-count">{recentActivities.filter(a => a.type === 'delete').length}</div>
                <div className="activity-label">Deleted</div>
              </div>
            </div>
            <div className="activity-stat">
              <FaDatabase className="activity-icon system" />
              <div>
                <div className="activity-count">{recentActivities.filter(a => a.type === 'system').length}</div>
                <div className="activity-label">System</div>
              </div>
            </div>
          </div>
        </div>

        {/* Data Health Check */}
        <div className="analytics-card health-card">
          <h3><FaCheck /> Data Health Check</h3>
          <div className="health-checks">
            <div className={`health-item ${stats.total > 0 ? 'healthy' : 'warning'}`}>
              <span className="health-icon">{stats.total > 0 ? '✅' : '⚠️'}</span>
              <span>Database has content</span>
              <span className="health-value">{stats.total} proverbs</span>
            </div>
            <div className={`health-item ${stats.chinese === stats.total ? 'healthy' : 'warning'}`}>
              <span className="health-icon">{stats.chinese === stats.total ? '✅' : '⚠️'}</span>
              <span>All entries have Chinese text</span>
              <span className="health-value">{stats.chinese}/{stats.total}</span>
            </div>
            <div className={`health-item ${stats.english > stats.total * 0.8 ? 'healthy' : 'warning'}`}>
              <span className="health-icon">{stats.english > stats.total * 0.8 ? '✅' : '⚠️'}</span>
              <span>Good English translation coverage</span>
              <span className="health-value">{Math.round((stats.english / stats.total) * 100)}%</span>
            </div>
            <div className={`health-item ${proverbs.filter(p => p.pinyin).length === stats.total ? 'healthy' : 'warning'}`}>
              <span className="health-icon">{proverbs.filter(p => p.pinyin).length === stats.total ? '✅' : '⚠️'}</span>
              <span>Pinyin pronunciation available</span>
              <span className="health-value">{proverbs.filter(p => p.pinyin).length}/{stats.total}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Super Admin Only - Advanced Analytics */}
      {isSuperAdmin() && (
        <div className="advanced-analytics">
          <h3><FaCrown /> Super Admin Analytics</h3>
          <div className="admin-analytics-grid">
            <div className="admin-card">
              <h4>System Performance</h4>
              <div className="performance-metrics">
                <div>Database Size: {(JSON.stringify(proverbs).length / 1024).toFixed(2)} KB</div>
                <div>Last Updated: {new Date().toLocaleString()}</div>
                <div>Active Sessions: 1</div>
              </div>
            </div>
            
            <div className="admin-card">
              <h4>User Activity</h4>
              <div className="user-metrics">
                <div>Total Users: 2</div>
                <div>Super Admins: 1</div>
                <div>Admins: 1</div>
                <div>Recent Activities: {recentActivities.length}</div>
              </div>
            </div>

            <div className="admin-card">
              <h4>Content Distribution</h4>
              <div className="content-metrics">
                <div>Unique Chinese Characters: {[...new Set(proverbs.flatMap(p => p.chinese_proverb ? p.chinese_proverb.split('') : []))].length}</div>
                <div>Unique English Words: {[...new Set(proverbs.flatMap(p => p.english_translation ? p.english_translation.toLowerCase().split(/\s+/) : []))].length}</div>
                <div>Average Translations per Proverb: {Math.round((stats.english + stats.french + stats.arabic) / stats.total) || 0}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsPage;
